import React from 'react';
import './MediaFactory.css';

/**
 * DataTag component - displays a single live data tag on the media factory
 * 
 * @param {Object} props
 * @param {string} props.label - Label to display above the value
 * @param {any} props.value - Live value from MQTT
 * @param {Object} props.position - { x: number, y: number } as percentages
 * @param {Object} props.style - { color: string, showLabel: boolean }
 * @param {boolean} props.isHot - Whether the value recently changed
 * @param {boolean} props.isEditing - Whether we're in edit mode
 * @param {function} props.onClick - Click handler (for edit mode)
 * @param {function} props.onDragStart - Drag start handler (for edit mode)
 * @param {function} props.onDrag - Drag handler (for edit mode)
 * @param {function} props.onDragEnd - Drag end handler (for edit mode)
 */
const DataTag = ({
  label,
  value,
  position = { x: 50, y: 50 },
  style = {},
  isHot = false,
  isEditing = false,
  onClick,
  onDragStart,
  onDrag,
  onDragEnd,
  onPointerDown,
}) => {
  const {
    color = '#F28E2B',
    showLabel = true,
  } = style;

  // Format value for display
  const formatValue = (val) => {
    if (val === undefined || val === null) return '---';
    if (typeof val === 'number') {
      // Round to 2 decimal places if needed
      return Number.isInteger(val) ? val : val.toFixed(2);
    }
    if (typeof val === 'boolean') return val ? 'ON' : 'OFF';
    if (typeof val === 'string') return val;
    return JSON.stringify(val);
  };

  const handleDragStart = (e) => {
    if (!isEditing) return;
    e.dataTransfer.setData('text/plain', ''); // Required for Firefox
    if (onDragStart) onDragStart(e);
  };

  const handleDrag = (e) => {
    if (!isEditing) return;
    if (onDrag) onDrag(e);
  };

  const handleDragEnd = (e) => {
    if (!isEditing) return;
    if (onDragEnd) onDragEnd(e);
  };

  return (
    <div
      className={`data-tag-wrapper ${isEditing ? 'data-tag-editing' : ''}`}
      style={{
        left: `${position.x}%`,
        top: `${position.y}%`,
        '--tag-color': color,
        ...(style.cursor ? { cursor: style.cursor } : {}),
      }}
      draggable={isEditing}
      onDragStart={handleDragStart}
      onDrag={handleDrag}
      onDragEnd={handleDragEnd}
      onClick={onClick}
      onPointerDown={onPointerDown}
    >
      {/* Tag bubble */}
      <div
        className={`data-tag ${isHot ? 'data-tag-hot' : ''}`}
      >
        <svg viewBox="0 0 14 14" fill="white" width="14" height="14" style={{ flexShrink: 0 }}>
          <path d="M7 12.25c-1.468 0-2.71-.226-3.726-.678C2.258 11.12 1.75 10.568 1.75 9.917V4.083c0-.641.513-1.192 1.539-1.648C4.314 1.978 5.551 1.75 7 1.75s2.686.228 3.711.685c1.026.457 1.539 1.007 1.539 1.648v5.834c0 .651-.508 1.203-1.524 1.655-1.016.452-2.258.678-3.726.678zM7 5.265a9.81 9.81 0 002.61-.372c.875-.248 1.366-.513 1.473-.795-.107-.282-.596-.549-1.466-.802A9.635 9.635 0 007 2.917c-.885 0-1.752.124-2.603.372-.851.248-1.344.515-1.48.81.136.291.63.559 1.48.81A9.81 9.81 0 007 5.265zm0 2.902c.408 0 .802-.02 1.181-.058.38-.04.741-.095 1.087-.168a8.59 8.59 0 00.977-.27c.306-.107.586-.228.838-.365V5.556a8.59 8.59 0 01-.838.365 8.36 8.36 0 01-.977.27c-.346.073-.713.128-1.087.168A11.14 11.14 0 017 6.417c-.408 0-.807-.02-1.196-.058a10.1 10.1 0 01-1.1-.168 8.36 8.36 0 01-.97-.27 6.25 6.25 0 01-.817-.365v1.75c.243.136.523.258.817.365.301.107.624.197.97.27.345.073.712.128 1.1.168.39.039.784.058 1.196.058zm0 2.916c.447 0 .902-.034 1.364-.102.461-.068.887-.158 1.276-.27.389-.112.714-.238.977-.38.262-.14.418-.284.467-.43V8.473a8.588 8.588 0 01-.839.365 8.36 8.36 0 01-.977.27c-.345.072-.712.128-1.086.167A11.14 11.14 0 017 9.333c-.408 0-.807-.02-1.196-.058a10.1 10.1 0 01-1.1-.168 8.36 8.36 0 01-.97-.27 6.25 6.25 0 01-.817-.365v1.445c.049.146.202.287.46.423.257.136.58.26.97.372.388.112.816.202 1.283.27.467.068.924.102 1.37.102z" />
        </svg>
        {showLabel && label && (
          <span className="data-tag-label">{label}:</span>
        )}
        <span className="data-tag-value">
          {formatValue(value)}
        </span>
      </div>

      {/* Connector stem + dot */}
      <div className="data-tag-stem" />
      <div className="data-tag-ring" />
    </div>
  );
};

export default DataTag;

